import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { Create, RaRecord, Toolbar, useNotify, useRefresh } from "react-admin";
import { useTranslate } from "react-admin";

import { useErrorHandler } from "../hooks/useErrorHandler";
import { useImageUploadHandler } from "../hooks/useImageUploadHandler";
import { useOrganizationContext } from "../hooks/useOrganizationContext";
import { color } from "../theme/designTokens";
import { BaseModalProps } from "../types/modal";
import { CloseButton, SaveButton } from "./buttons";
import { PractitionerForm, PractitionerFormData, serializePractitionerLocationScope } from "./PractitionerForm";

const PractitionerCreateToolbar = ({ onCancel }: { onCancel: () => void }) => {
  return (
    <Toolbar>
      <SaveButton />
      <CloseButton onClick={onCancel} />
    </Toolbar>
  );
};

export const PractitionerCreate = ({ open, onClose }: BaseModalProps) => {
  const { organizationId } = useOrganizationContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const handleError = useErrorHandler();
  const translate = useTranslate();

  const { handleImageUpload } = useImageUploadHandler<PractitionerFormData>();

  const handleTransform = (data: PractitionerFormData) => {
    if (!organizationId) {
      throw new Error(translate("create.noOrganizationSelected"));
    }

    const locationIds = serializePractitionerLocationScope(data);
    if (!locationIds) {
      throw new Error(translate("practitionerLocationScope.selectedRequired"));
    }

    const dataWithOrganization = {
      ...data,
      organizationId,
      locationIds,
    };
    delete dataWithOrganization.locationScope;

    return handleImageUpload(dataWithOrganization);
  };

  const handleSuccess = (data: RaRecord) => {
    notify("create.practitioner.success", { type: "success" });
    if ((data as { warningCode?: string | null }).warningCode === "USER_HAS_NO_EMAIL") {
      notify("create.practitioner.noEmailWarning", { type: "warning" });
    }
    refresh();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle sx={{ backgroundColor: color.background }}>{translate("create.practitioner.title")}</DialogTitle>
      <DialogContent>
        <Create
          resource="Practitioner"
          transform={handleTransform}
          mutationOptions={{
            onError: error => handleError(error, translate("create.practitioner.error")),
            onSuccess: handleSuccess,
          }}
        >
          <PractitionerForm toolbar={<PractitionerCreateToolbar onCancel={onClose} />} />
        </Create>
      </DialogContent>
    </Dialog>
  );
};
